import Events from 'root/helpers/events';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import FormControllerInit from 'root/main/components/forms/formControllerInit';
import { getHashFromURL } from 'root/utils/url/urlUtils';
import LightboxLoader from 'root/main/components/forms/core/lightboxLoader';
import FormUI from 'root/main/components/forms/lightbox/FormUI';
import FormElementsController from 'root/main/components/forms/lightbox/FormElementsController';
import Loader from 'root/main/components/loader/loader';
import React from 'react';
import ReactDOM from 'react-dom';
import LoadingIndicator from 'root/helpers/loadingIndicator';

interface LoadingIndicatorProps {
    form: HTMLElement | null | undefined;
}

export default class FormLoader {
    readonly #lightboxEl: HTMLElement;
    readonly #formName: string;
    readonly #spinnerEl: HTMLElement;
    #formType: string;
    #formEl: HTMLFormElement | null;
    #formOuterHTML: HTMLElement | null;

    constructor(lightboxEl: HTMLElement, lightboxURL: string) {
        this.#lightboxEl = lightboxEl;
        this.#formName = this.#getFormNameFromUrl(lightboxURL);
        this.#formEl = null;
        this.#formOuterHTML = null;
        this.#spinnerEl = this.#createSpinner();
        this.#formType = 'leadform';
    }

    #getFormNameFromUrl(lightboxURL: string) {
        return getHashFromURL(lightboxURL).split('|')[1] ?? '';
    }

    #addFormToPage(data: string) {
        if (this.#lightboxEl) {
            const formHTMLWrapper = document.createElement('div');
            formHTMLWrapper.innerHTML = data;
            this.#formOuterHTML = this.#lightboxEl.insertBefore(
                formHTMLWrapper,
                this.#lightboxEl.firstChild
            );

            // this is necessary here to hide the FOUC when the form is loaded
            (
                LoadingIndicator as (
                    props: LoadingIndicatorProps
                ) => { hide: () => void } | null
            )({
                form: this.#formEl,
            })?.hide();
        }
    }

    #createSpinner() {
        const spinnerContainer = document.createElement('div');
        spinnerContainer.className = 'loading';
        ReactDOM.render(React.createElement(Loader), spinnerContainer);
        this.#lightboxEl.insertBefore(spinnerContainer, this.#lightboxEl.firstChild);
        return spinnerContainer;
    }

    #getTwigData(callback: (data: string) => void) {
        const lightboxLoader = new LightboxLoader(this.#formName);
        lightboxLoader.onSuccess(callback).onError(callback).getFile();
    }

    #initializeFormEl() {
        this.#formEl = this.#lightboxEl.querySelector('[data-form*="ls-form"]');
        if (this.#formEl) {
            const lightboxFormLoaded = new CustomEvent('lightboxFormLoaded');
            /* this custom event is used in the email only for triggering actions when the lightbox has been initialized */
            Events.fire('lightboxOpened');
            document.body.dispatchEvent(lightboxFormLoaded);

            /* We are dynamically importing this here because once a new form is created inside the lightbox, it will not
            have access to formController if it has not been initialized previously on the page may be due to the absence
            of a submit button. By this we are ensuring no matter what that the formController will be initialized inside the lightbox. */
            FormControllerInit(this.#formEl);

            import(
                /*webpackChunkName: "agnosticFormController"*/ 'root/main/components/forms/agnosticFormControllerInit'
            ).then(module => module.default(this.#formEl));
        } else {
            LoggingHandler().logError(
                `leadform: form element is undefined on uri: ${window.location.pathname.substring(
                    1
                )}`,
                {
                    searchTerm: 'lightbox-forms-error',
                    tags: ['forms'],
                    fileName: 'FormLoader.js',
                }
            );
        }
    }

    #setFormType(formType: string) {
        this.#formType = formType;
    }

    load(e: Event) {
        this.#getTwigData(data => {
            if (this.#spinnerEl) {
                this.#spinnerEl.style.display = 'none';
            }
            if (data) {
                !data.includes('leadform') && this.#setFormType('form');
                this.#addFormToPage(data);
                this.#initializeFormEl();

                const formUI = new FormUI(
                    this.#formName,
                    this.#lightboxEl,
                    this.#formEl,
                    this.#formOuterHTML,
                    this.#formType
                );
                formUI.render();

                const formElementsCtl = new FormElementsController(this.#formEl);
                formElementsCtl.init(e);
            } else {
                console.log(`error loading lightbox form. data="${data}"`);
                LoggingHandler().logError('User unsuccessfully opened lightbox form', {
                    searchTerm: 'failed-to-load-lightbox-form',
                    tags: ['forms'],
                    fileName: 'formController.js',
                    additionalData: { data },
                });
            }
        });
    }
}
